/**
 * Builds EEC energy label image URLs served by Audi MediaService.
 *
 * Shared by the two places that produce such a URL — the `energy-label` lambda (class-derived) and
 * the `vehicle-data` lambda (baked onto the vehicle for markets that need a CO2 figure) — so the
 * two can never drift on the path shape.
 */

import { requiresCo2Segment } from './marketRules';

export const MEDIA_SERVICE_EEC_BASE = 'https://mediaservice.audi.com/media/eec-label';

// MediaService rejects nothing, so these guard the path itself: a class or locale carrying `/` or
// `..` would otherwise let a caller reshape the URL.
const EFFICIENCY_CLASS_PATTERN = /^[A-Za-z0-9+]+$/;
const COUNTRY_PATTERN = /^[a-z]{2}$/i;
const LOCALE_PATTERN = /^[a-z]{2}(-[a-z]{2})?$/i;

export interface IEecLabelUrlParams {
  /** ISO 3166-1 alpha-2 country, e.g. `fr`. */
  country: string;
  /** Locale segment, e.g. `fr-fr`. Ignored by MediaService for CO2-segment countries. */
  locale: string;
  efficiencyClass: string | undefined;
  /** WLTP combined CO2 in g/km. Required for {@link CO2_SEGMENT_COUNTRIES}, ignored elsewhere. */
  co2?: number;
}

/**
 * Returns the label URL, or `null` when it cannot be built correctly.
 *
 * `null` means "show no label". That is deliberately preferred over a partially-correct URL: an
 * absent label is a gap, whereas a wrong one misstates the vehicle's emissions.
 */
export function buildEecLabelUrl({ country, locale, efficiencyClass, co2 }: IEecLabelUrlParams): string | null {
  if (!efficiencyClass || !EFFICIENCY_CLASS_PATTERN.test(efficiencyClass)) return null;
  if (!COUNTRY_PATTERN.test(country) || !LOCALE_PATTERN.test(locale)) return null;

  const base = `${MEDIA_SERVICE_EEC_BASE}/${country}/${locale}/${efficiencyClass}`;

  if (!requiresCo2Segment(country)) return base;

  // `co2 === 0` is a real BEV reading, so presence is tested rather than truthiness.
  if (typeof co2 !== 'number' || !Number.isFinite(co2) || co2 < 0) return null;

  // A decimal segment renders the same placeholder as a missing one, so it must be an integer.
  return `${base}/${Math.round(co2)}`;
}
